
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { CSSProperties, useEffect, useState } from "react";
import { FabrixIcon, translate, useDebounce, useIcon } from "..";
import { SortDirection } from "@nt-portal/common-sdk";


export interface SortItem {
    label: string;
    value: any;
}

export interface FilterGroupProps {
    sortValues: SortItem[];
    style?: CSSProperties;
    className?: string;
    filter?: string;
    orderBy?: string;
    orderDir?: SortDirection;
    onChange: (filter: string, orderBy: string, orderDir: SortDirection) => void;
    onClear?: () => void;
    hideOrderBy?: boolean
}

/**
 * Provides a unified control for filtering and sorting lists with debounce support.
 * @param props - Filter text, sort options, and handlers for notifying parent components.
 * @returns A composed filter panel featuring search, order selection, and clear actions.
 */
export function FilterGroup(props: FilterGroupProps) {

    const icon = useIcon();
    const [_filter, setFilter] = useState<string>(props.filter ?? '')
    const [_orderBy, setOrderBy] = useState<string>(props.orderBy ?? '')
    const [_orderDir, setOrderDir] = useState<SortDirection>(props.orderDir ?? SortDirection.ASC)
    const [_sortOptions, setSortOptions] = useState<SortItem[]>([])
    const debounceText = useDebounce(_filter, 500);

    useEffect(() => {
        setFilter((prev) => prev !== props.filter ? props.filter ?? '' : prev)
    }, [props.filter]);

    useEffect(() => {
        setOrderBy((prev) => prev !== props.orderBy ? props.orderBy ?? '' : prev)
    }, [props.orderBy]);

    useEffect(() => {
        setOrderDir((prev) => prev !== props.orderDir ? props.orderDir ?? SortDirection.ASC : prev)
    }, [props.orderDir]);

    useEffect(() => {
        setSortOptions(props.sortValues)
    }, [props.sortValues])

    useEffect(() => {
        if (props.onChange && _filter !== undefined && _orderBy !== undefined && _orderDir !== undefined) {
            props.onChange(_filter ?? '', _orderBy ?? '', _orderDir)
        }
    }, [debounceText, _orderBy, _orderDir])

    /**
     * Resets the filter text locally or delegates clearing to external handlers.
     */
    const clearFilter = () => {
        if (props.onClear)
            props.onClear();
        else
            setFilter('')
    }

    /**
     * Updates the active order-by field when the dropdown selection changes.
     * @param e - PrimeReact dropdown change event.
     */
    const handleOrderByChanged = (e: any) => {
        if (e.value)
            setOrderBy(e.value)
    }

    return (
        <div className={`${props.className ?? ''} fbx-filter-group`} style={props.style}>
            {!props.hideOrderBy && (
                <>
                    <Dropdown value={_orderBy} className="fbx-filter-group-orderby" options={_sortOptions} optionLabel="label" optionValue="value"
                        placeholder={translate('ORDER_BY')} onChange={handleOrderByChanged}
                        itemTemplate={(option) => {
                            return <span >{option.label}</span>
                        }} />

                    <Button className="fbx-toolbar-button fbx-filter-sort-button"
                        icon={icon(_orderDir === SortDirection.DESC ? FabrixIcon.sortDesc : FabrixIcon.sortAsc)}
                        onClick={() => setOrderDir(_orderDir === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC)}></Button>
                </>)}

            <div className="fbx-filter-text" >
                <span className="p-input-icon-right">
                    <i className={icon(FabrixIcon.search)} />
                    <InputText type="text" placeholder={translate("SEARCH")} value={_filter} onChange={(e) => setFilter(e.target.value)} />
                </span>
            </div>

            <Button className="fbx-toolbar-button fbx-filter-clear" icon={icon(FabrixIcon.filterClear)} onClick={clearFilter}></Button>
        </div>
    )
}
